import React, {
    useRef,
    useState,
    useEffect,
    useCallback,
    useLayoutEffect,
} from 'react';
import {useAppDispatch, useAppSelector} from 'store/customer';
import styled from 'styled-components';
import {
    getActiveGroupPosition,
    getIsDragging,
    getShowCabinets,
    setRoomPlannerRoomIndex,
} from 'components/customer/RoomPlanner/store/roomPlannerSlice';
import {useRoomPlannerContext} from 'components/customer/RoomPlanner/components/RoomPlannerContext';
import {useRoomPreview} from 'components/customer/RoomPlanner/components/helpers/RoomPreview';
import {
    hasDoors,
    CabinetGroup,
} from 'components/customer/RoomPlanner/helpers/doorDetection';
import {useJobContext} from 'contexts';
import * as THREE from 'three';
import {PartialJob} from 'shared/types/PartialJob';
import {
    useIsMobileLandscape,
    useIsPortraitDevices,
} from 'shared/helpers/DeviceSize';
import PlaceholderFloatingMenu from './PlaceholderFloatingMenu';
import CameraSetup from 'components/customer/RoomPlanner/lib/use3DRPCameraSetup';

interface FloatingComponentProps {
    containerRef: React.RefObject<HTMLDivElement>;
    children?: React.ReactNode;
}

const FloatingContainer = styled.div`
    position: absolute;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    min-width: 200px;
    user-select: none;
    color: #f1f1f1;
    pointer-events: none;
`;

const TopRow = styled.div`
    background: #1e1e1e; /* Dark mode */
    display: flex;
    align-items: center;
    justify-content: flex-start;
    height: 36px;
    box-shadow: 2px 2px 6px rgba(0, 0, 0, 0.4);
    pointer-events: auto;
`;

const DragHandle = styled.div`
    background: #333; /* Slightly lighter for contrast */
    border-radius: 4px 0 0 4px;
    cursor: grab;
    width: 30px;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    user-select: none;
`;

const IconContainer = styled.div`
    display: flex;
    flex: 1;
    justify-content: space-between;
    align-items: center;
    padding: 0 8px;
    user-select: none;

    svg {
        width: 18px;
        height: 18px;
        color: #f1f1f1;
        cursor: pointer;
    }

    svg:last-child {
        color: #888;
    }
`;

interface Position {
    x: number;
    y: number;
}

interface CustomEvent {
    clientX: number;
    clientY: number;
}

const FloatingComponent = ({
    containerRef,
    children,
}: FloatingComponentProps) => {
    const floatingRef = useRef<HTMLDivElement>(null);
    const [bounds, setBounds] = useState({width: 0, height: 0});
    const [position, setPosition] = useState<Position>({x: 0, y: 0});
    const [isPositioned, setIsPositioned] = useState(false);
    const hasBeenDragged = useRef(false);

    const isPortraitDevices = useIsPortraitDevices();
    const isMobileLandscape = useIsMobileLandscape();
    const isDraggingRef = useRef(false);
    const startXRef = useRef(0);
    const startYRef = useRef(0);
    const offsetXRef = useRef(0);
    const offsetYRef = useRef(0);

    const dispatch = useAppDispatch();
    const activeGroupPosition = useAppSelector(getActiveGroupPosition);
    const {selectedCabinetGroup} = useRoomPlannerContext();
    const isRoomPreview = useRoomPreview();
    const cabinetHasDoors = selectedCabinetGroup
        ? hasDoors(selectedCabinetGroup as CabinetGroup)
        : false;

    const isDragging = useAppSelector(getIsDragging);
    const showCabinets = useAppSelector(getShowCabinets);
    const isPlaceholder = Boolean(
        selectedCabinetGroup?.userData?.isPlaceholder
    );
    const {roomIndex: contextRoomIndex} = useJobContext() as PartialJob;

    useEffect(() => {
        // In preview mode, roomIndex is set by useRoomPlanner from preview data
        // Don't override it with undefined contextRoomIndex
        if (!isRoomPreview) {
            dispatch(setRoomPlannerRoomIndex(contextRoomIndex ?? null));
        }
        return () => {
            if (!isRoomPreview) {
                dispatch(setRoomPlannerRoomIndex(null));
            }
        };
    }, [dispatch, contextRoomIndex, isRoomPreview]);

    // Measure the canvas container whenever it actually gets a size (staging
    // often mounts this before the WebGL canvas has been laid out).
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const updateBounds = () => {
            const canvas = container.querySelector('canvas');
            const width = canvas?.clientWidth || container.clientWidth || 0;
            const height = canvas?.clientHeight || container.clientHeight || 0;
            setBounds((prev) =>
                prev.width === width && prev.height === height
                    ? prev
                    : {width, height}
            );
        };

        updateBounds();

        const resizeObserver = new ResizeObserver(updateBounds);
        resizeObserver.observe(container);

        const mutationObserver = new MutationObserver(() => {
            const canvas = container.querySelector('canvas');
            if (canvas) {
                resizeObserver.observe(canvas);
            }
            updateBounds();
        });
        mutationObserver.observe(container, {childList: true, subtree: true});

        const canvas = container.querySelector('canvas');
        if (canvas) {
            resizeObserver.observe(canvas);
        }

        return () => {
            resizeObserver.disconnect();
            mutationObserver.disconnect();
        };
    }, [containerRef]);

    // resize observer to keep component in bounds
    useEffect(() => {
        if (!floatingRef.current) return;

        const observer = new ResizeObserver(() => {
            if (
                !floatingRef.current ||
                bounds.width < 10 ||
                bounds.height < 10
            ) {
                return;
            }
            setPosition((prev) => ({
                x: clampX(prev.x),
                y: clampY(prev.y),
            }));
        });

        observer.observe(floatingRef.current);
        return () => observer.disconnect();
    }, [
        bounds.width,
        bounds.height,
        floatingRef?.current?.clientWidth ?? 0,
        floatingRef?.current?.clientHeight ?? 0,
    ]);

    const clampNumber = (n: number, min: number, max: number): number => {
        return Math.max(min, Math.min(n, max));
    };
    const clampX = (x: number): number =>
        clampNumber(
            x,
            0,
            bounds.width - (floatingRef.current?.clientWidth ?? 0)
        );

    const clampY = (y: number): number =>
        clampNumber(
            y,
            0,
            bounds.height - (floatingRef.current?.clientHeight ?? 0)
        );

    const repositionComponent = useCallback((): boolean => {
        if (!selectedCabinetGroup) {
            return false;
        }

        const container = containerRef.current;
        const canvas = container?.querySelector('canvas');
        const width =
            canvas?.clientWidth || container?.clientWidth || bounds.width;
        const height =
            canvas?.clientHeight || container?.clientHeight || bounds.height;

        // Without a real layout size, stored halfWidth/halfHeight from select
        // time can be 0 and the menu would clamp to the top-left.
        if (width < 10 || height < 10) {
            return false;
        }

        const camera = CameraSetup.camera;
        let screenPos = activeGroupPosition?.screenPos;
        let cabinetHeight = activeGroupPosition?.cabinetHeight;

        if (camera) {
            const worldPos = new THREE.Vector3();
            selectedCabinetGroup.getWorldPosition(worldPos);
            screenPos = worldPos.clone().project(camera);
            const box = new THREE.Box3().setFromObject(selectedCabinetGroup);
            cabinetHeight = box.max.y - box.min.y;
        }

        if (!screenPos || cabinetHeight == null) {
            return false;
        }

        const halfWidth = width / 2;
        const halfHeight = height / 2;
        const x = screenPos.x * halfWidth + halfWidth;
        const y = -screenPos.y * halfHeight + halfHeight;

        const cabinetHitbox = new THREE.Box3().setFromObject(
            selectedCabinetGroup
        );
        const dimensions = new THREE.Vector3();
        cabinetHitbox.getSize(dimensions);

        const menuWidth = floatingRef.current?.clientWidth ?? 200;
        const menuHeight = floatingRef.current?.clientHeight ?? 36;
        const computedX = x + dimensions.x * 0.9;
        const computedY = y - cabinetHeight * 0.45;

        setPosition({
            x: clampNumber(computedX, 0, Math.max(0, width - menuWidth)),
            y: clampNumber(computedY, 0, Math.max(0, height - menuHeight)),
        });
        return true;
    }, [
        activeGroupPosition,
        bounds.height,
        bounds.width,
        containerRef,
        selectedCabinetGroup,
    ]);

    // Reposition when the cabinet, its stored screen position, or the canvas
    // size becomes available. Do this in useLayoutEffect so we never paint at 0,0.
    useLayoutEffect(() => {
        if (hasBeenDragged.current) {
            return;
        }

        setIsPositioned(repositionComponent());
    }, [
        selectedCabinetGroup,
        activeGroupPosition,
        bounds.width,
        bounds.height,
        repositionComponent,
    ]);

    const move = useCallback(
        (event: CustomEvent) => {
            if (!isDraggingRef.current || !floatingRef.current) return;
            const deltaX = event.clientX - startXRef.current;
            const deltaY = event.clientY - startYRef.current;

            const newX = clampX(offsetXRef.current + deltaX);
            const newY = clampY(offsetYRef.current + deltaY);

            setPosition({x: newX, y: newY});
            if (!hasBeenDragged.current) hasBeenDragged.current = true;
        },
        [clampX, clampY]
    );

    const dragStart = useCallback(
        (event: CustomEvent, touch = false) => {
            if (!floatingRef.current) return;
            isDraggingRef.current = true;

            startXRef.current = event.clientX;
            startYRef.current = event.clientY;
            offsetXRef.current = position.x;
            offsetYRef.current = position.y;

            const handleMouseMove = (e: MouseEvent) => {
                move(e);
            };

            const handleTouchMove = (e: TouchEvent) => {
                if (!isDraggingRef.current || !floatingRef.current) return;

                const touch = e.touches[0];
                move(touch);
            };

            const handleMouseUp = () => {
                isDraggingRef.current = false;
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
            };

            const handleTouchEnd = () => {
                isDraggingRef.current = false;
                document.removeEventListener('touchmove', handleTouchMove);
                document.removeEventListener('touchend', handleTouchEnd);
            };

            if (touch) {
                document.addEventListener('touchmove', handleTouchMove);
                document.addEventListener('touchend', handleTouchEnd);
            } else {
                document.addEventListener('mousemove', handleMouseMove);
                document.addEventListener('mouseup', handleMouseUp);
            }
        },
        [position, move]
    );

    const handleMouseDown = useCallback(
        (e: React.MouseEvent) => {
            dragStart(e);
        },
        [dragStart]
    );

    const handleTouchStart = useCallback(
        (e: React.TouchEvent) => {
            const touch = e.touches[0];

            dragStart(touch, true);
        },
        [dragStart]
    );

    if (isPortraitDevices || isMobileLandscape) {
        return null;
    }

    if (
        !selectedCabinetGroup ||
        isDragging ||
        !activeGroupPosition ||
        !showCabinets ||
        (isRoomPreview && !cabinetHasDoors)
    ) {
        return null;
    }

    return (
        <FloatingContainer
            ref={floatingRef}
            style={{
                top: position.y,
                left: position.x,
                visibility: isPositioned ? 'visible' : 'hidden',
            }}>
            <TopRow>
                <DragHandle
                    onMouseDown={handleMouseDown}
                    onTouchStart={handleTouchStart}>
                    ⋮⋮
                </DragHandle>
                <IconContainer>
                    {isPlaceholder && !isRoomPreview ? (
                        <PlaceholderFloatingMenu />
                    ) : (
                        children
                    )}
                </IconContainer>
            </TopRow>
        </FloatingContainer>
    );
};

export default FloatingComponent;
